import { useCallback, useEffect, type MutableRefObject } from 'react';

import type { CalendarContextProps } from '../contexts/CalendarContext.js';
import {
  NUMBER_OF_MONTHS_PER_ROW,
  NUMBER_OF_WEEKDAYS,
  NUMBER_OF_YEARS,
  NUMBER_OF_YEARS_PER_ROW,
} from '../types/calendarConfiguration.js';
import type {
  CalendarViewType,
  CalendarNavigationPayload,
} from '../types/shared-types.js';

export interface UseCalendarNavigationProps
  extends Pick<CalendarContextProps, 'dispatch' | 'state'> {
  calendarRef: MutableRefObject<HTMLElement | null>;
}

// Set of keys that should not prevent the default action
const allowedKeys = new Set(['Tab', 'Enter', 'NumpadEnter', ' ']);

// Set of keys for navigating the calendar body
// Pressing one of those keys should move the focus to the calendar body
const calendarNavigationKeyCodes = new Set([
  'ArrowUp',
  'ArrowRight',
  'ArrowDown',
  'ArrowLeft',
  'PageUp',
  'PageDown',
  'Home',
  'End',
]);

const getMultipler = (view: CalendarViewType) => {
  switch (view) {
    case 'day':
      return NUMBER_OF_WEEKDAYS;
    case 'month':
      return NUMBER_OF_MONTHS_PER_ROW;
    case 'year':
      return NUMBER_OF_YEARS_PER_ROW;
  }
};

/** Custom hook for adding keyboard support to the calendar. */
export function useCalendarNavigation({
  calendarRef,
  state,
  dispatch,
}: UseCalendarNavigationProps) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!allowedKeys.has(event.key)) {
        event.preventDefault();
      }

      // Dispatch state change to actually focus the active calendar body element
      if (calendarNavigationKeyCodes.has(event.key)) {
        dispatch({ type: 'isCalendarBodyFocusedChange', payload: true });
      }

      const multiplier = getMultipler(state.currentView);
      const payload = {} as CalendarNavigationPayload;

      switch (event.key) {
        case 'ArrowUp':
          // Go to previous row
          payload.amount = -1 * multiplier;
          break;
        case 'ArrowDown':
          // Go to next week
          payload.amount = 1 * multiplier;
          break;
        case 'ArrowLeft':
          // Go to previous item
          payload.amount = -1;
          break;
        case 'ArrowRight':
          // Go to next item
          payload.amount = 1;
          break;
        case 'PageUp':
          if (state.currentView === 'day') {
            payload.amount = -1;
            // Go to previous month. If ALT or SHIFT key is pressed go to previous year instead
            payload.unit = event.altKey || event.shiftKey ? 'year' : 'month';
          } else if (state.currentView === 'month') {
            payload.amount = -1;
            payload.unit = 'year';
          } else {
            payload.amount = -NUMBER_OF_YEARS;
          }
          break;
        case 'PageDown':
          if (state.currentView === 'day') {
            payload.amount = 1;
            // Go to previous month. If ALT or SHIFT key is pressed go to previous year instead
            payload.unit = event.altKey || event.shiftKey ? 'year' : 'month';
          } else if (state.currentView === 'month') {
            payload.amount = 1;
            payload.unit = 'year';
          } else {
            payload.amount = NUMBER_OF_YEARS;
          }
          break;
        // For day view only
        case 'Home':
          // Go to the first day of the week.
          if (state.currentView === 'day') {
            payload.amount = 'startOfWeek';
          }
          break;
        case 'End':
          // Go to the last day of the week.
          if (state.currentView === 'day') {
            payload.amount = 'endOfWeek';
          }
          break;
      }

      if (payload.amount) {
        dispatch({ type: 'navigateCurrentView', payload });
      }
    },
    [dispatch, state.currentView],
  );

  // Register event listeners to listen for key events
  useEffect(() => {
    const calendarRefCurrent = calendarRef.current;
    calendarRefCurrent?.addEventListener('keydown', handleKeyDown);

    return () => {
      calendarRefCurrent?.removeEventListener('keydown', handleKeyDown);
    };
  }, [calendarRef, handleKeyDown]);
}
